/**
 * Saved views storage — preset di filtri associati a una route specifica.
 *
 * Shape: { id, name, route, search, createdAt }
 *   - id:     string univoco
 *   - name:   etichetta libera (es. "KR a rischio")
 *   - route:  pathname (es. "/oggi", "/timeline", "/obiettivo/abc")
 *   - search: querystring senza "?" (es. "area=business&stato=on_track")
 *
 * Persistenza in `localStorage.qp.saved_views`. Single-user app → no backend sync.
 */
const KEY = 'qp.saved_views';

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

export function loadViews() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (err) {
    console.warn('[savedViews] loadViews fallito:', err);
    return [];
  }
}

function saveAll(views) {
  try {
    localStorage.setItem(KEY, JSON.stringify(views));
    window.dispatchEvent(new CustomEvent('qp:saved-views-changed'));
  } catch (err) {
    // Solitamente quota piena
    console.warn('[savedViews] saveAll fallito (quota?):', err);
  }
}

export function addView({ name, route, search }) {
  const v = {
    id: uid(),
    name: (name || '').trim() || 'Vista senza nome',
    route: route || '/',
    search: (search || '').replace(/^\?/, ''),
    createdAt: new Date().toISOString(),
  };
  const all = loadViews();
  all.unshift(v);
  saveAll(all.slice(0, 20)); // limita a 20
  return v;
}

export function deleteView(id) {
  saveAll(loadViews().filter((v) => v.id !== id));
}

export function renameView(id, name) {
  saveAll(loadViews().map((v) => v.id === id ? { ...v, name } : v));
}

export function toHref(v) {
  return v.search ? `${v.route}?${v.search}` : v.route;
}

// ── Share token (compact, URL-safe base64) ────────────────────────────────────
// Forma compatta { n: name, r: route, s: search } → base64url
function b64urlEncode(str) {
  const b64 = btoa(unescape(encodeURIComponent(str)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlDecode(token) {
  const b64 = token.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
  return decodeURIComponent(escape(atob(b64 + pad)));
}

/**
 * Codifica una vista in token compatto base64url.
 * @param {{name:string, route:string, search:string}} v
 */
export function encodeShareToken(v) {
  return b64urlEncode(JSON.stringify({
    n: v.name || '',
    r: v.route || '/',
    s: (v.search || '').replace(/^\?/, ''),
  }));
}

/**
 * Decodifica un token in vista. Ritorna null se invalido.
 */
export function decodeShareToken(token) {
  try {
    const obj = JSON.parse(b64urlDecode(token));
    if (typeof obj?.r !== 'string') return null;
    return {
      name: typeof obj.n === 'string' ? obj.n : 'Vista condivisa',
      route: obj.r,
      search: typeof obj.s === 'string' ? obj.s : '',
    };
  } catch (err) {
    console.warn('[savedViews] decode shared view fallito:', err);
    return null;
  }
}

/**
 * Costruisce l'URL condivisibile per una vista.
 * Esempio: https://app.tld/oggi?v=eyJuIjoi...
 */
export function buildShareUrl(v, origin) {
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  const token = encodeShareToken(v);
  return `${base}${v.route}?v=${token}`;
}

/**
 * Esporta tutte le viste in un oggetto JSON serializzabile.
 * Shape: { app: "quantumplanner", kind: "saved_views", version: 1, exportedAt, views: [...] }
 */
export function exportViews() {
  return {
    app: 'quantumplanner',
    kind: 'saved_views',
    version: 1,
    exportedAt: new Date().toISOString(),
    views: loadViews(),
  };
}

/**
 * Avvia il download del file JSON con tutte le viste salvate.
 */
export function downloadViewsFile() {
  const data = exportViews();
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `quantumplanner-viste-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function isValidView(v) {
  return (
    v && typeof v === 'object' &&
    typeof v.name === 'string' &&
    typeof v.route === 'string' &&
    typeof v.search === 'string'
  );
}

/**
 * Importa viste da un payload (oggetto già parsato).
 *
 * @param {object} payload  Oggetto export (anche un semplice array di viste)
 * @param {'merge'|'replace'} mode  Default 'merge': aggiunge senza duplicare (chiave route+search)
 * @returns {{ added: number, skipped: number, total: number }}
 */
export function importViews(payload, mode = 'merge') {
  let incoming = [];
  if (Array.isArray(payload)) {
    incoming = payload;
  } else if (payload && Array.isArray(payload.views)) {
    incoming = payload.views;
  } else {
    throw new Error('Formato file non valido');
  }

  const normalized = incoming
    .filter(isValidView)
    .map((v) => ({
      id: uid(),
      name: (v.name || '').trim() || 'Vista senza nome',
      route: v.route,
      search: (v.search || '').replace(/^\?/, ''),
      createdAt: v.createdAt || new Date().toISOString(),
    }));

  if (mode === 'replace') {
    saveAll(normalized.slice(0, 20));
    return { added: normalized.length, skipped: 0, total: normalized.length };
  }

  const existing = loadViews();
  const seen = new Set(existing.map((v) => `${v.route}?${v.search}`));
  let added = 0;
  let skipped = 0;
  const merged = [...existing];
  for (const v of normalized) {
    const key = `${v.route}?${v.search}`;
    if (seen.has(key)) {
      skipped += 1;
      continue;
    }
    seen.add(key);
    merged.unshift(v);
    added += 1;
  }
  saveAll(merged.slice(0, 20));
  return { added, skipped, total: merged.length };
}
